import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { CostGroupEnum, ProjectContractTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { useSetIsWorkOrderChangeRequired } from 'pages/Project/BimProject/hooks/useSetIsWorkOrderChangeRequired';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { bimAddWorkOrderOtherForActGql, bimAddWorkOrderOthersGql } from '../bimAddOrderOtherQueries';

import { prepareBimOtherData } from './convertHelper';

interface IUseAddToBimWorkOrder {
    isFrameContract: boolean;
    handlerSuccess: () => void;
    constructionTypeId?: number;
    workOrderId: number;
    checkedIds: {
        [key: number | string]: {
            [key: number | string]: any;
        };
    };
    workOrderStatus?: WorkOrderStatusEnum;
    workOrderObjectId?: number;
}

const requestId = 'addToBimWorkOrder';

export const useAddToBimWorkOrder = (props: IUseAddToBimWorkOrder) => {
    const {
        isFrameContract,
        handlerSuccess,
        constructionTypeId,
        workOrderId,
        checkedIds,
        workOrderStatus,
        workOrderObjectId,
    } = props;

    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const isInProgress = useMemo(() => workOrderStatus === WorkOrderStatusEnum.InProgress, [workOrderStatus]);

    const dispatch = useDispatch();
    const otherPrices = prepareBimOtherData({
        checkedObjects: checkedIds,
    });

    const [, addStatus, , addToBimWorkOrder] = useRequest({
        requestId,
        query: bimAddWorkOrderOthersGql,
        withEffect: false,
        dependenciesUpdater: handlerSuccess,
    });

    const [addWorkOrderOtherForActData, addWorkOrderOtherForActStatus, , addWorkOrderOtherForAct] = useRequest({
        requestId,
        query: bimAddWorkOrderOtherForActGql,
        withEffect: false,
        dependenciesUpdater: handlerSuccess,
    });

    useSetIsWorkOrderChangeRequired({
        useData: addWorkOrderOtherForActData?.bimAddWorkOrderOtherForAct?.data,
        status: addWorkOrderOtherForActStatus,
    });

    const [resetAddData] = useResetStoreData({
        requestId,
    });

    useEffect(
        () => () => {
            resetAddData(undefined);
        },
        [resetAddData],
    );

    const isAddInProgress = useMemo(
        () =>
            isInProgress
                ? addWorkOrderOtherForActStatus === RequestStatus.FETCHING
                : addStatus === RequestStatus.FETCHING,
        [addStatus, addWorkOrderOtherForActStatus, isInProgress],
    );

    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: workOrderId,
            orderStatus: workOrderStatus,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        }),
        [
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            workOrderId,
            workOrderStatus,
        ],
    );

    const addToWorkOrderChangeHandler = useCallback(() => {
        if (isInProgress) {
            sendGpnAnalyticsEvent({
                componentId: 'rim_NZ_add to order other button_pressed',
                ...analyticsData,
                addType: CostGroupEnum.Others,
            });

            dispatch(
                addWorkOrderOtherForAct({
                    contractOtherCostIds: Object.keys(checkedIds).map((k) => Number(k)),
                    workOrderObjectId,
                }),
            );
        } else if (constructionTypeId) {
            sendGpnAnalyticsEvent({
                componentId: 'rim_act KC_add to act KC other button_pressed',
                ...analyticsData,
                addType: CostGroupEnum.Others,
            });

            dispatch(
                addToBimWorkOrder({
                    contractType: isFrameContract ? ProjectContractTypeEnum.Frame : ProjectContractTypeEnum.Normal,
                    input: {
                        constructionTypeId,
                        otherPrices,
                        workOrderId,
                    },
                }),
            );
        }
    }, [
        addToBimWorkOrder,
        addWorkOrderOtherForAct,
        analyticsData,
        checkedIds,
        constructionTypeId,
        dispatch,
        isFrameContract,
        isInProgress,
        otherPrices,
        sendGpnAnalyticsEvent,
        workOrderId,
        workOrderObjectId,
    ]);

    return { addToWorkOrderChangeHandler, isAddInProgress };
};
